import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteLoaderData,
} from "react-router"
import { I18nProvider as ReactAriaI18nProvider } from "react-aria-components/I18nProvider"
import { MainError } from "source/MainError"
import { GoogleTagManager } from "source/shared/GoogleTagManager"
import { webBootPropsContext } from "source/webBootPropsMiddleware"
import { Route } from "./+types/root"

// Reads the props `webBootPropsMiddleware` already fetched via middleware
// (see RootLayout.ssr.tsx), so we can render the CSRF meta tag server-side.
export function loader({ context }: Route.LoaderArgs) {
  return context.get(webBootPropsContext)
}

export function shouldRevalidate() {
  return false
}

export function Layout({ children }: { children: React.ReactNode }) {
  const csrfToken =
    useRouteLoaderData<typeof loader>("root")?.configuration.csrfToken

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1.0" />
        <meta name="theme-color" content="#FAFAFA" />
        <meta name="apple-mobile-web-app-title" content="Church Center" />
        <link rel="manifest" href="/manifest.json" />
        {csrfToken && <meta name="csrf-token" content={csrfToken} />}
        <Meta />
        <Links />
        <GoogleTagManager />
      </head>
      <body
        className="sans-serif"
        data-color-scheme="light"
        data-contrast="low"
      >
        <a
          href="/platform-admin"
          rel="nofollow"
          style={{ display: "none" }}
          aria-hidden="true"
        >
          Platform Admin
        </a>
        <a href="#main_page_content" className="screen-reader-text">
          Skip to content
        </a>
        {children}
        <noscript>
          <div style={{ margin: "4rem auto", maxWidth: 600, padding: "1rem" }}>
            Church Center requires JavaScript to be enabled.
            <br />
            <br />
            Here are some{" "}
            <a href="https://www.enable-javascript.com/">
              instructions to enable JavaScript in your web browser
            </a>
            .
          </div>
        </noscript>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export default function Root() {
  return (
    <ReactAriaI18nProvider locale="en-US">
      <Outlet />
    </ReactAriaI18nProvider>
  )
}

export function ErrorBoundary() {
  return (
    <ReactAriaI18nProvider locale="en-US">
      <MainError onRestart={() => window.location.reload()} />
    </ReactAriaI18nProvider>
  )
}

export function HydrateFallback() {
  const themeScript = `
    if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("theme") === "dark") {
      document.documentElement.classList.add("hydrate-dark");
    }
  `

  const styles = `
    @keyframes shimmer {
      0% { background-position: -400px 0; }
      100% { background-position: 400px 0; }
    }
    .hydrate-fallback-shimmer {
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 800px 100%;
      animation: shimmer 1.5s infinite linear;
      border-radius: 4px;
    }
    .hydrate-fallback-header {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 136px;
      background-color: #fafafa;
      border-bottom: 1px solid #f2f2f2;
    }
    .hydrate-fallback-header-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      max-width: 900px;
      padding: 24px 32px 24px 24px;
    }
    .hydrate-fallback-body {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
      background-color: #ffffff;
    }
    .hydrate-fallback-nav {
      display: none;
    }
    .hydrate-fallback-nav-burger {
      display: block;
    }
    .hydrate-dark .hydrate-fallback-shimmer {
      background: linear-gradient(90deg, #2a2a2a 25%, #3a3a3a 50%, #2a2a2a 75%);
      background-size: 800px 100%;
    }
    .hydrate-dark .hydrate-fallback-header {
      background-color: #1a1a1a;
      border-bottom-color: #2a2a2a;
    }
    .hydrate-dark .hydrate-fallback-body {
      background-color: #111111;
    }
    .hydrate-fallback-loading {
      font-size: 16px;
      color: hsl(0, 0%, 81%);
      display: flex;
      justify-content: center;
      padding: 1.5rem;
    }
    .hydrate-dark .hydrate-fallback-loading {
      color: hsl(0, 0%, 32%);
    }
    .hydrate-fallback-dot-1 {
      animation: dot1 1.4s infinite step-end;
    }
    .hydrate-fallback-dot-2 {
      animation: dot2 1.4s infinite step-end;
    }
    .hydrate-fallback-dot-3 {
      animation: dot3 1.4s infinite step-end;
    }
    @keyframes dot1 {
      0%, 25% { visibility: hidden; }
      25.1%, 100% { visibility: visible; }
    }
    @keyframes dot2 {
      0%, 50% { visibility: hidden; }
      50.1%, 100% { visibility: visible; }
    }
    @keyframes dot3 {
      0%, 75% { visibility: hidden; }
      75.1%, 100% { visibility: visible; }
    }
    @media screen and (min-width: 720px) {
      .hydrate-fallback-nav {
        display: flex;
      }
      .hydrate-fallback-nav-burger {
        display: none;
      }
      .hydrate-fallback-loading {
        padding: 2rem;
      }
    }
  `

  return (
    <>
      <script dangerouslySetInnerHTML={{ __html: themeScript }} />
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="hydrate-fallback-body">
        {/* Header skeleton */}
        <header className="hydrate-fallback-header">
          <div className="hydrate-fallback-header-content">
            {/* Org logo/title placeholder */}
            <div
              className="hydrate-fallback-shimmer"
              style={{
                width: 200,
                height: 20,
              }}
            />
            {/* Nav links placeholder — hidden on mobile */}
            <div
              className="hydrate-fallback-nav"
              style={{ gap: 24, alignItems: "center" }}
            >
              <div
                className="hydrate-fallback-shimmer"
                style={{ width: 48, height: 14 }}
              />
              <div
                className="hydrate-fallback-shimmer"
                style={{ width: 56, height: 14 }}
              />
              <div
                className="hydrate-fallback-shimmer"
                style={{ width: 40, height: 14 }}
              />
              <div
                className="hydrate-fallback-shimmer"
                style={{ width: 52, height: 14 }}
              />
              <div
                className="hydrate-fallback-shimmer"
                style={{ width: 44, height: 14 }}
              />
            </div>
            {/* Nav burger placeholder — hidden on desktop */}
            <div
              className="hydrate-fallback-shimmer hydrate-fallback-nav-burger"
              style={{ width: 40, height: 36 }}
            />
          </div>
        </header>

        {/* Loading text */}
        <div className="hydrate-fallback-loading">
          <span>
            Loading
            <span className="hydrate-fallback-dot-1">.</span>
            <span className="hydrate-fallback-dot-2">.</span>
            <span className="hydrate-fallback-dot-3">.</span>
          </span>
        </div>
      </div>
    </>
  )
}
